import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, setCsrfToken } from "../http/client";
import type {
  StaffSession,
  ModulePermission,
  PermissionModule,
  PortalRenewal,
} from "./types";

// GET /v1/portal/session (SPEC-201 §3): usuario, perfil y permisos
// efectivos. "Ocultar no es autorizar" — este contexto solo decide qué
// mostrar, la autorización real la resuelve el servidor en cada petición
// (ADR-011).

interface SessionContextValue {
  // `null` = "sin sesión, a propósito" (tras signOut); `undefined` = "todavía
  // no se supo" (antes de la primera carga). Ver el comentario de `signOut`
  // más abajo sobre por qué no alcanza con `undefined` para lo primero.
  session: StaffSession | null | undefined;
  isLoading: boolean;
  isError: boolean;
  hasPermission: (
    module: PermissionModule,
    action?: keyof Omit<ModulePermission, "module">,
  ) => boolean;
  signOut: () => Promise<void>;
  // Devuelve la promesa de TanStack Query para que un consumidor pueda
  // esperar a que la sesión en caché quede actualizada antes de navegar
  // (ej. ChangePasswordScreen tras limpiar mustChangePassword) — un
  // `void` acá dejaría el refetch "flotando" y el consumidor navegaría
  // antes de que RequirePasswordChange vea el valor nuevo.
  refetch: () => Promise<unknown>;
  /**
   * Fija en caché una sesión recién obtenida fuera del ciclo normal de
   * `GET /v1/portal/session` (SPEC-221: la respuesta de sign-in ya trae la
   * sesión completa). Misma queryKey que `fetchSession` para que el resto
   * del árbol la vea sin refetch adicional.
   */
  applySession: (session: StaffSession) => void;
  applyRenewal: (renewal: PortalRenewal) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

async function fetchSession(): Promise<StaffSession> {
  return apiFetch<StaffSession>("/v1/portal/session");
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["portal", "session"],
    queryFn: fetchSession,
    retry: false,
    staleTime: 60_000,
    // T2 (t2-foundation, qa-bug-matrix-2026-09.md §6): sin sesión es un
    // resultado normal de este query (visitante sin autenticar, o sesión
    // vencida) — `query.isError` ya lo resuelve más abajo redirigiendo a
    // `/access`. El fallback global de `main.tsx` (`QueryCache.onError`) no
    // sabe distinguir "todavía no hay sesión" de un 500 real; sin este
    // `meta`, cada carga sin sesión mostraba un toast de error encima del
    // formulario de acceso.
    meta: { toastHandled: true },
  });

  const value = useMemo<SessionContextValue>(() => {
    const session = query.data;
    if (session?.csrfToken) {
      setCsrfToken(session.csrfToken);
    }
    return {
      session,
      isLoading: query.isLoading,
      isError: query.isError,
      hasPermission: (module, action = "canView") => {
        const entry = session?.permissions.find((p) => p.module === module);
        return entry ? entry[action] : false;
      },
      // FINDING (dos bugs reales encadenados, ambos confirmados con test
      // automatizado — SessionProvider.test.tsx):
      // 1. `removeQueries` borra por completo la entrada de caché y deja al
      //    observer de este `useQuery` sin volver a reaccionar a ningún
      //    `setQueryData` posterior — tras cerrar sesión y volver a
      //    loguearse en la misma pestaña, `SessionProvider` quedaba
      //    congelado con la sesión anterior.
      // 2. El reemplazo obvio, `setQueryData(key, undefined)`, es un no-op
      //    documentado de TanStack Query (`queryClient.js`: "si el valor es
      //    undefined, la query no se actualiza") — parecía funcionar en las
      //    pruebas manuales solo porque el login siguiente sobreescribía el
      //    dato viejo con uno real; el estado "deslogueado" en sí nunca se
      //    aplicaba. Un test que verificara el estado inmediato después de
      //    `signOut()` (sin login posterior) lo atrapó.
      // Se usa `null` como valor real y distinto de `undefined`: sí pasa por
      // `setData` (no activa el no-op) y no rompe la reactividad del
      // observer como `removeQueries`.
      signOut: async () => {
        try {
          await apiFetch<void>("/v1/portal/access/sign-out", {
            method: "POST",
          });
        } finally {
          setCsrfToken(null);
          // La sesiÃ³n conserva su observer: removeQueries sobre esta clave
          // rompe la reactividad del provider (regresiÃ³n documentada arriba).
          // Se cancelan todas las requests del portal, incluida la sesiÃ³n:
          // una respuesta de A iniciada antes del logout no puede volver a
          // poblar ese observer. Solo las queries de datos se remueven abajo.
          await queryClient.cancelQueries({
            predicate: (cachedQuery) => cachedQuery.queryKey[0] === "portal",
          });
          queryClient.removeQueries({
            predicate: (cachedQuery) =>
              cachedQuery.queryKey[0] === "portal" &&
              cachedQuery.queryKey[1] !== "session",
          });
          queryClient.setQueryData(["portal", "session"], null);
        }
      },
      refetch: () => query.refetch(),
      applySession: (newSession) => {
        setCsrfToken(newSession.csrfToken);
        queryClient.setQueryData(["portal", "session"], newSession);
      },
      applyRenewal: (renewal) => {
        setCsrfToken(renewal.csrfToken);
        queryClient.setQueryData<StaffSession | null>(
          ["portal", "session"],
          (current) =>
            current
              ? {
                  ...current,
                  expiresAt: renewal.expiresAt,
                  csrfToken: renewal.csrfToken,
                }
              : current,
        );
      },
    };
  }, [query, queryClient]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession debe usarse dentro de SessionProvider");
  }
  return ctx;
}

// Variante que no exige `SessionProvider`: para primitivos de `portal/common`
// (p. ej. `SecuritySegmented`) que también se usan en pruebas o galerías
// fuera del árbol de sesión real y solo necesitan un dato best-effort (acá,
// `isAdministrator` para decidir qué tab mostrar) — nunca para decidir
// autorización real, que sigue siendo del servidor (ADR-011).
export function useOptionalSession(): SessionContextValue | undefined {
  return useContext(SessionContext);
}
